import { useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarCheck, ChevronLeft, ChevronRight, FileSignature, KeyRound, Pencil, Plus, Trash2 } from 'lucide-react';
import { ContractCreateModal, type ContractPreset } from '../components/forms/ContractModal';
import { ReservationFormModal, type ReservationDraft } from '../components/forms/ReservationFormModal';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { ConfirmDialog, Modal } from '../components/ui/Modal';
import { useDeleteReservation, useHandoverInternal, useReservationCalendar, useReservationDriver, useReservations } from '../hooks/useApi';
import { useUrlFilters } from '../hooks/useUrlFilters';
import { errorMessage, reservationsApi } from '../services/api';
import { toast } from '../store';
import type { CalendarEntry, Reservation } from '../types';
import { cn, driverName, formatDate, formatMonth, toDateInput } from '../utils/helpers';
import { locale, t } from '../i18n';

// Status colours (green = free, yellow = reserved, red = lent out); reserved also carries stripes
const FREE: CSSProperties = { backgroundColor: 'rgb(12 163 12 / 0.22)' };
const RESERVED: CSSProperties = {
  backgroundColor: '#fab219',
  backgroundImage: 'repeating-linear-gradient(45deg, rgb(255 255 255 / 0.45) 0 2px, transparent 2px 6px)',
};
const OCCUPIED: CSSProperties = { backgroundColor: '#d03b3b' };
/** "Po", "Mon", "Mo" – short weekday in the current language */
const weekday = (d: Date) => {
  const name = d.toLocaleDateString(locale(), { weekday: 'short' }).replace(/\.$/, '');
  return name.charAt(0).toUpperCase() + name.slice(1);
};

function monthDays(period: string): string[] {
  const [year, month] = period.split('-').map(Number);
  const count = new Date(year, month, 0).getDate();
  return Array.from({ length: count }, (_, i) => toDateInput(new Date(year, month - 1, i + 1)));
}

function shiftMonth(period: string, delta: number): string {
  const [year, month] = period.split('-').map(Number);
  return toDateInput(new Date(year, month - 1 + delta, 1)).slice(0, 7);
}

function personOf(r: Reservation): string {
  return r.driver ? driverName(r.driver) : r.person_name ?? '–';
}

export function Reservations() {
  const [filters, setFilters] = useUrlFilters({ month: toDateInput().slice(0, 7) });
  const [draft, setDraft] = useState<ReservationDraft | null>(null);
  const [editing, setEditing] = useState<Reservation | null>(null);
  const [handingOver, setHandingOver] = useState<Reservation | null>(null);
  const [contractPreset, setContractPreset] = useState<ContractPreset | null>(null);
  const [deleting, setDeleting] = useState<Reservation | null>(null);
  const remove = useDeleteReservation();

  return (
    <>
      <PageHeader
        title={t('Rezervácie vozidiel')}
        subtitle={t('Kalendár voľných, rezervovaných a požičaných vozidiel')}
        actions={
          <Button onClick={() => setDraft({})}>
            <Plus className="h-4 w-4" />  {t('Rezervovať vozidlo')}
          </Button>
        }
      />
      <AvailabilityCalendar
        period={filters.month}
        onPeriod={(month) => setFilters({ month })}
        onFreeDay={(carId, day) => setDraft({ car_id: carId, date_from: day, date_to: day })}
        onReservation={(id) => reservationsApi.get(id).then(setEditing).catch((err) => toast.error(errorMessage(err)))}
      />
      <UpcomingReservations onEdit={setEditing} onHandover={setHandingOver} onDelete={setDeleting} />

      <ReservationFormModal isOpen={!!draft} draft={draft} onClose={() => setDraft(null)} />
      <ReservationFormModal isOpen={!!editing} reservation={editing} onClose={() => setEditing(null)} />
      <HandoverDialog reservation={handingOver} onClose={() => setHandingOver(null)} onContract={setContractPreset} />
      <ContractCreateModal isOpen={!!contractPreset} preset={contractPreset} onClose={() => setContractPreset(null)} />
      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        loading={remove.isPending}
        title={t('Zrušiť rezerváciu')}
        confirmText={t('Zrušiť rezerváciu')}
        message={
          deleting &&
          t('Zrušiť rezerváciu {plate} pre {person} ({dateFrom} – {dateTo})?', { plate: deleting.car?.license_plate, person: personOf(deleting), dateFrom: formatDate(deleting.date_from), dateTo: formatDate(deleting.date_to) }) +
            (deleting.handover_mode ? t(' Vozidlo už bolo odovzdané – pridelenie zostane, ukončite ho v Pridelení.') : '')
        }
        onConfirm={async () => {
          try {
            await remove.mutateAsync(deleting!.id);
            toast.success(t('Rezervácia bola zrušená'));
          } catch (err) {
            toast.error(errorMessage(err));
          }
          setDeleting(null);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------- calendar
type DragMode = 'move' | 'start' | 'end';

/** A reservation being dragged: its dates as they would be after dropping. */
interface DragPreview {
  id: number;
  car_id: number;
  date_from: string;
  date_to: string;
  invalid: string | null;
  saving?: boolean;
}

/** Consecutive days of one calendar row with the same state, drawn as one cell. */
interface Segment {
  start: number;
  span: number;
  entry?: CalendarEntry;
}

function addDays(day: string, delta: number): string {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() + delta);
  return toDateInput(d);
}

function rowSegments(days: string[], entries: CalendarEntry[]): Segment[] {
  const segments: Segment[] = [];
  days.forEach((day, i) => {
    const hits = entries.filter((e) => e.date_from <= day && day <= e.date_to);
    // A car handed over wins over a reservation of the same day
    const entry = hits.find((e) => e.kind === 'occupied') ?? hits[0];
    const last = segments[segments.length - 1];
    if (entry && last?.entry === entry) last.span++;
    else segments.push({ start: i, span: 1, entry });
  });
  return segments;
}

function AvailabilityCalendar({
  period,
  onPeriod,
  onFreeDay,
  onReservation,
}: {
  period: string;
  onPeriod: (period: string) => void;
  onFreeDay: (carId: number, day: string) => void;
  onReservation: (id: number) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const days = useMemo(() => monthDays(period), [period]);
  const { data, isFetching } = useReservationCalendar(days[0], days[days.length - 1]);
  const today = toDateInput();
  const [year, month] = period.split('-').map(Number);
  const headerRef = useRef<HTMLTableRowElement>(null);
  const [preview, setPreview] = useState<DragPreview | null>(null);
  // Set right after a drag so the click that follows the mouse release does not open the form
  const justDragged = useRef(false);

  const byCar = useMemo(() => {
    const map = new Map<number, CalendarEntry[]>();
    for (const entry of data?.entries ?? []) map.set(entry.car_id, [...(map.get(entry.car_id) ?? []), entry]);
    return map;
  }, [data]);

  const entriesOf = (carId: number): CalendarEntry[] => {
    const entries = byCar.get(carId) ?? [];
    if (preview?.car_id !== carId) return entries;
    return entries.map((e) => (e.reservation_id === preview.id ? { ...e, date_from: preview.date_from, date_to: preview.date_to } : e));
  };

  const saveDates = async (entry: CalendarEntry, dates: { date_from: string; date_to: string }, undo: boolean) => {
    try {
      await reservationsApi.update(entry.reservation_id!, dates);
      await queryClient.invalidateQueries({ queryKey: ['reservations'] });
      const message = `${entry.label}: ${formatDate(dates.date_from)} – ${formatDate(dates.date_to)}`;
      if (undo) toast.success(t('Presun bol vrátený – {message}', { message }));
      else
        toast.success(t('Rezervácia presunutá – {message}', { message }), {
          label: t('Späť'),
          onClick: () => saveDates(entry, { date_from: entry.date_from, date_to: entry.date_to }, true),
        });
    } catch (err) {
      toast.error(errorMessage(err, t('Rezerváciu sa nepodarilo presunúť')));
    }
  };

  const startDrag = (event: ReactPointerEvent, entry: CalendarEntry, mode: DragMode) => {
    // Mouse and pen only: on touch screens a swipe scrolls the calendar and a tap opens the form
    if (event.button !== 0 || event.pointerType === 'touch' || !headerRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const columns = [...headerRef.current.querySelectorAll<HTMLElement>('th[data-day]')].map((th) => th.getBoundingClientRect());
    const columnAt = (x: number) => {
      const index = columns.findIndex((rect) => x < rect.right);
      return index === -1 ? columns.length - 1 : index;
    };
    const origin = columnAt(event.clientX);
    const others = (byCar.get(entry.car_id) ?? []).filter((e) => e.kind === 'reserved' && e.reservation_id !== entry.reservation_id);
    let current: DragPreview | null = null;

    const onMove = (e: PointerEvent) => {
      const delta = columnAt(e.clientX) - origin;
      let from = entry.date_from;
      let to = entry.date_to;
      if (mode === 'move') [from, to] = [addDays(from, delta), addDays(to, delta)];
      else if (mode === 'start') from = addDays(from, delta) > to ? to : addDays(from, delta);
      else to = addDays(to, delta) < from ? from : addDays(to, delta);
      if (from === entry.date_from && to === entry.date_to) {
        current = null;
        setPreview(null);
        return;
      }
      const clash = others.find((o) => o.date_from <= to && o.date_to >= from);
      const intoPast = (from !== entry.date_from && from < today) || (to !== entry.date_to && to < today);
      current = {
        id: entry.reservation_id!,
        car_id: entry.car_id,
        date_from: from,
        date_to: to,
        invalid: intoPast ? t('Rezerváciu nemožno presunúť do minulosti') : clash ? t('Vozidlo je v tomto období už rezervované pre {label}', { label: clash.label }) : null,
      };
      setPreview(current);
    };

    const onUp = async () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.removeProperty('cursor');
      const dropped = current;
      // No change: the click that follows opens the reservation
      if (!dropped) return;
      justDragged.current = true;
      window.setTimeout(() => (justDragged.current = false));
      if (dropped.invalid) {
        toast.error(dropped.invalid);
        setPreview(null);
        return;
      }
      setPreview({ ...dropped, saving: true });
      await saveDates(entry, { date_from: dropped.date_from, date_to: dropped.date_to }, false);
      setPreview(null);
    };

    document.body.style.cursor = mode === 'move' ? 'grabbing' : 'ew-resize';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const describe = (entry: CalendarEntry) =>
    `${entry.kind === 'occupied' ? t('Požičané') : t('Rezervované')}: ${entry.label}${entry.detail ? ` – ${entry.detail}` : ''} (${formatDate(entry.date_from)} – ${
      entry.open_ended ? t('bez plánovaného konca') : formatDate(entry.date_to)
    })`;

  return (
    <Card padding="none" className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-2">
          <button className="icon-btn" onClick={() => onPeriod(shiftMonth(period, -1))} aria-label={t('Predchádzajúci mesiac')}>
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="min-w-[10rem] text-center text-lg font-semibold">{formatMonth(year, month)}</h2>
          <button className="icon-btn" onClick={() => onPeriod(shiftMonth(period, 1))} aria-label={t('Nasledujúci mesiac')}>
            <ChevronRight className="h-5 w-5" />
          </button>
          <Button variant="ghost" size="sm" onClick={() => onPeriod(today.slice(0, 7))}>
            {t('Dnes')}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-secondary-700">
          <LegendItem style={FREE} label={t('voľné')} />
          <LegendItem style={RESERVED} label={t('rezervované')} />
          <LegendItem style={OCCUPIED} label={t('požičané')} />
        </div>
      </div>
      <div className={cn('overflow-x-auto border-t border-secondary-100', isFetching && !preview && 'opacity-70')}>
        <table className="w-full border-separate text-xs" style={{ borderSpacing: 2 }}>
          <thead>
            <tr ref={headerRef}>
              <th scope="col" className="sticky left-0 z-10 min-w-[9rem] bg-white px-3 py-2 text-left font-medium text-secondary-500">
                {t('Vozidlo')}
              </th>
              {days.map((day) => {
                const d = new Date(`${day}T12:00:00`);
                const weekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <th
                    key={day}
                    data-day={day}
                    scope="col"
                    className={cn(
                      'min-w-[1.75rem] rounded px-0 py-1 text-center font-medium',
                      weekend ? 'text-secondary-400' : 'text-secondary-600',
                      day === today && 'bg-primary-600 text-white',
                    )}
                  >
                    <span className="block tabular-nums">{d.getDate()}</span>
                    <span className="block text-[10px] font-normal">{weekday(d)}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data?.cars.map((car) => (
              <tr key={car.id}>
                <th scope="row" className="sticky left-0 z-10 bg-white px-3 py-1 text-left font-normal">
                  <Link to={`/cars/${car.id}`} className="font-mono text-sm font-medium text-primary-700 hover:underline">
                    {car.license_plate}
                  </Link>
                  <span className="block truncate text-secondary-500">
                    {car.brand} {car.model}
                    {car.status === 'service' && ' · v servise'}
                  </span>
                </th>
                {rowSegments(days, entriesOf(car.id)).map(({ start, span, entry }) => {
                  if (!entry) {
                    const day = days[start];
                    const label = t('{plate}, {day}: voľné – kliknutím rezervujete', { plate: car.license_plate, day: formatDate(day) });
                    return (
                      <td key={day} className="p-0">
                        <button
                          type="button"
                          title={label}
                          aria-label={label}
                          onClick={() => onFreeDay(car.id, day)}
                          className="block h-9 w-full min-w-[1.75rem] rounded-[4px] transition hover:brightness-95"
                          style={FREE}
                        />
                      </td>
                    );
                  }
                  const reserved = entry.kind === 'reserved';
                  const dragged = reserved && preview?.id === entry.reservation_id ? preview : null;
                  // Reservations that already ended stay where they are
                  const original = reserved ? byCar.get(car.id)?.find((e) => e.reservation_id === entry.reservation_id) : undefined;
                  const movable = !!original && original.date_to >= today && !dragged?.saving;
                  const startsHere = entry.date_from >= days[0];
                  const endsHere = entry.date_to <= days[days.length - 1];
                  const label = describe(entry) + (movable ? t(' – potiahnutím presuniete, za okraj zmeníte dĺžku') : '');
                  return (
                    <td key={`${entry.kind}-${entry.reservation_id ?? entry.assignment_id}-${start}`} colSpan={span} className="p-0">
                      <div className="relative">
                        <button
                          type="button"
                          title={label}
                          aria-label={label}
                          onPointerDown={movable ? (e) => startDrag(e, original, 'move') : undefined}
                          onClick={() => {
                            if (justDragged.current) return;
                            if (entry.reservation_id) onReservation(entry.reservation_id);
                            else navigate(`/assignments?car_id=${car.id}`);
                          }}
                          className={cn(
                            'block h-9 w-full min-w-[1.75rem] select-none truncate px-1.5 text-left text-[11px] font-medium transition hover:brightness-95',
                            startsHere ? 'rounded-l-[4px]' : 'rounded-l-none',
                            endsHere ? 'rounded-r-[4px]' : 'rounded-r-none',
                            reserved ? 'text-secondary-900' : 'text-white',
                            movable && 'cursor-grab',
                            dragged && 'shadow-md ring-2',
                            dragged && (dragged.invalid ? 'opacity-60 ring-red-600' : 'ring-primary-600'),
                            dragged?.saving && 'animate-pulse',
                          )}
                          style={reserved ? RESERVED : OCCUPIED}
                        >
                          {span >= 3 && entry.label}
                        </button>
                        {movable && startsHere && (
                          <span
                            aria-hidden="true"
                            onPointerDown={(e) => startDrag(e, original, 'start')}
                            className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize rounded-l-[4px] hover:bg-black/20"
                          />
                        )}
                        {movable && endsHere && (
                          <span
                            aria-hidden="true"
                            onPointerDown={(e) => startDrag(e, original, 'end')}
                            className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize rounded-r-[4px] hover:bg-black/20"
                          />
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data && !data.cars.length && <EmptyState icon={<CalendarCheck className="h-10 w-10" />} title={t('Žiadne aktívne vozidlá')} />}
      <p className="border-t border-secondary-100 px-4 py-2 text-xs text-secondary-500">
        {t('Kliknite na voľný deň pre novú rezerváciu, na rezervovaný pre úpravu. Rezerváciu môžete myšou potiahnuť na iné dni, potiahnutím okraja zmeníte jej dĺžku (ukončené rezervácie sa presunúť nedajú). Požičané = odovzdané so zmluvou alebo interne (Pridelenie).')}
      </p>
    </Card>
  );
}

function LegendItem({ style, label }: { style: CSSProperties; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-3.5 w-3.5 rounded-[3px]" style={style} aria-hidden="true" /> {label}
    </span>
  );
}

// ---------------------------------------------------------------- list
function UpcomingReservations({
  onEdit,
  onHandover,
  onDelete,
}: {
  onEdit: (r: Reservation) => void;
  onHandover: (r: Reservation) => void;
  onDelete: (r: Reservation) => void;
}) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useReservations({ upcoming: true, page, page_size: 20 });

  const columns: Column<Reservation>[] = [
    { key: 'period', header: t('Obdobie'), className: 'whitespace-nowrap', render: (r) => `${formatDate(r.date_from)} – ${formatDate(r.date_to)}` },
    {
      key: 'car',
      header: t('Vozidlo'),
      render: (r) => (
        <Link to={`/cars/${r.car_id}`} className="whitespace-nowrap hover:underline">
          <span className="font-mono font-medium text-primary-700">{r.car?.license_plate}</span>
          <span className="block text-xs text-secondary-500">
            {r.car?.brand} {r.car?.model}
          </span>
        </Link>
      ),
    },
    {
      key: 'person',
      header: t('Pre koho'),
      render: (r) => (
        <span>
          {personOf(r)}
          {!r.driver_id && <span className="block text-xs text-secondary-500">{t('mimo zoznamu pracovníkov')}</span>}
        </span>
      ),
    },
    { key: 'purpose', header: t('Účel'), render: (r) => <span className="line-clamp-2 max-w-xs text-sm">{r.purpose || '–'}</span> },
    {
      key: 'status',
      header: t('Stav'),
      className: 'whitespace-nowrap',
      render: (r) =>
        r.handover_mode === 'contract' ? (
          <Badge variant="danger" dot>
            {t('Odovzdané · zmluva {number}', { number: r.contract_number })}
          </Badge>
        ) : r.handover_mode === 'internal' ? (
          <Badge variant="danger" dot>
            {t('Odovzdané interne')}
          </Badge>
        ) : (
          <Badge variant="warning" dot>
            {t('Rezervované')}
          </Badge>
        ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right whitespace-nowrap',
      render: (r) => (
        <>
          {!r.handover_mode && (
            <Button size="sm" variant="outline" className="mr-1" onClick={() => onHandover(r)}>
              <KeyRound className="h-4 w-4" />  {t('Odovzdať')}
            </Button>
          )}
          <button className="icon-btn" onClick={() => onEdit(r)} title={t('Upraviť')} aria-label={t('Upraviť')}>
            <Pencil className="h-4 w-4" />
          </button>
          <button className="icon-btn-danger" onClick={() => onDelete(r)} title={t('Zrušiť rezerváciu')} aria-label={t('Zrušiť rezerváciu')}>
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      ),
    },
  ];

  return (
    <Card padding="none">
      <div className="p-5 pb-0">
        <CardHeader title={t('Aktuálne a nadchádzajúce rezervácie')} />
      </div>
      <DataTable
        data={data?.reservations}
        columns={columns}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        isFetching={isFetching}
        emptyMessage={<EmptyState icon={<CalendarCheck className="h-10 w-10" />} title={t('Žiadne nadchádzajúce rezervácie')} />}
        pagination={data && { page, pageSize: 20, total: data.total, onPageChange: setPage }}
      />
    </Card>
  );
}

// ---------------------------------------------------------------- handover
function HandoverDialog({
  reservation,
  onClose,
  onContract,
}: {
  reservation: Reservation | null;
  onClose: () => void;
  onContract: (preset: ContractPreset) => void;
}) {
  const internal = useHandoverInternal();
  const addDriver = useReservationDriver();
  const busy = internal.isPending || addDriver.isPending;

  const withContract = async () => {
    if (!reservation) return;
    try {
      // A contract needs a worker from the list: a name typed in by hand is added first
      const linked = reservation.driver_id ? reservation : await addDriver.mutateAsync(reservation.id);
      if (!reservation.driver_id) toast.info(t('{person} bol(a) pridaný(á) do zoznamu vodičov', { person: personOf(linked) }));
      onClose();
      onContract({
        reservation_id: linked.id,
        driver_id: linked.driver_id!,
        car_id: linked.car_id,
        date_from: linked.date_from,
        date_to: linked.date_to,
      });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const internally = async () => {
    if (!reservation) return;
    try {
      await internal.mutateAsync(reservation.id);
      toast.success(t('Vozidlo {plate} bolo interne požičané – {person}', { plate: reservation.car?.license_plate, person: personOf(reservation) }));
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, t('Vozidlo sa nepodarilo odovzdať')));
    }
  };

  const option = (icon: ReactNode, title: string, text: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="flex w-full items-start gap-3 rounded-lg border border-secondary-200 p-4 text-left transition-colors hover:border-primary-400 hover:bg-primary-50 disabled:opacity-60"
    >
      <span className="rounded-lg bg-primary-100 p-2 text-primary-700">{icon}</span>
      <span>
        <span className="block font-medium text-secondary-900">{title}</span>
        <span className="block text-sm text-secondary-500">{text}</span>
      </span>
    </button>
  );

  return (
    <Modal
      isOpen={!!reservation}
      onClose={onClose}
      busy={busy}
      title={t('Odovzdať vozidlo')}
      description={reservation && `${reservation.car?.license_plate} · ${personOf(reservation)} · ${formatDate(reservation.date_from)} – ${formatDate(reservation.date_to)}`}
    >
      <div className="space-y-3">
        {option(
          <FileSignature className="h-5 w-5" />,
          t('So zmluvou o nájme'),
          t('Pre živnostníka alebo externú osobu. Otvorí sa predvyplnená zmluva (Word) a vozidlo sa pridelí.'),
          withContract,
        )}
        {option(
          <KeyRound className="h-5 w-5" />,
          t('Interne, bez zmluvy'),
          t('Zamestnanec firmy – vozidlo sa iba pridelí na rezervované obdobie (Pridelenie).'),
          internally,
        )}
      </div>
    </Modal>
  );
}
